import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import trialService, { TrialStatusResponse } from '../../services/api/trialService';
import onboardingV2Service from '../../services/api/onboardingV2Service';
import { TRIAL_BANNER_STRINGS, useMetaLang } from '../../i18n/metaReviewLang';

const TrialBanner: React.FC = () => {
  const [lang] = useMetaLang();
  const strings = TRIAL_BANNER_STRINGS[lang];
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null);
  const [productDone, setProductDone] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchTrialStatus = async () => {
      try {
        const [status, progress] = await Promise.all([
          trialService.getTrialStatus(),
          onboardingV2Service.getProgress().catch(() => null),
        ]);
        if (mounted) {
          setTrialStatus(status);
          // 進捗APIの一時障害だけで既存の課金導線を消し続けないよう fail-open。
          // 読み込み中(null)は非表示にし、商品未登録バナーの一瞬の表示を防ぐ。
          setProductDone(progress?.product_done ?? true);
        }
      } catch {
        // Silently fail - banner is non-critical UI
      }
    };

    fetchTrialStatus();

    return () => {
      mounted = false;
    };
  }, []);

  // フリープラン時だけでなく、 課金後の Stripe trial 期間中も残り日数を表示する
  // (ユーザー要望タスク#2)。
  //
  // 表示条件 (Codex PR #487 [P2] への対応):
  //   - is_in_trial (Organization.trial_ends_at が未来) かつ
  //   - has_subscription=false (フリープラン) または is_stripe_trialing=true
  //     (有料契約中だが Stripe trial 期間中)
  //
  // has_subscription=true かつ is_stripe_trialing=false (= 通常の active な
  // 有料契約者) には表示しない。 そうしないと既に有料化済みのユーザーが
  // 「トライアル残り」と誤表示されるバグになる。
  if (!trialStatus || productDone !== true || !trialStatus.is_in_trial || dismissed) {
    return null;
  }
  if (trialStatus.has_subscription && !trialStatus.is_stripe_trialing) {
    return null;
  }

  const daysRemaining = trialStatus.days_remaining;
  const isUrgent = daysRemaining <= 2;

  return (
    <div
      className={`w-full py-2.5 px-4 text-center text-[13px] font-medium z-[999] ${
        isUrgent
          ? 'bg-amber-500 text-white'
          : 'bg-[#3eb298]/10 text-[#1d1926]'
      }`}
      style={{ position: 'relative' }}
    >
      <span>
        {isUrgent ? (
          <>{strings.remaining(daysRemaining)} - {strings.endingSoon}</>
        ) : (
          <>{strings.remaining(daysRemaining)}</>
        )}
      </span>
      <Link
        to="/plans"
        className={`ml-3 inline-flex items-center gap-1 font-semibold transition-opacity hover:opacity-80 no-underline ${
          isUrgent
            ? 'text-white underline'
            : 'text-[#3eb298]'
        }`}
      >
        {strings.upgrade}
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-opacity hover:opacity-70 ${
          isUrgent ? 'text-white/80' : 'text-[#1d1926]/40'
        }`}
        aria-label={strings.close}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default TrialBanner;
